import { useEffect, useState } from "react";
import { Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, normalizePhone } from "@/lib/api";
import { PhotoPicker } from "@/components/PhotoPicker";
import { ClerkPinModal } from "@/components/ClerkPinModal";
import { DateTimeQuickPicker } from "@/components/DateTimeQuickPicker";

const PHONE_RE = /^0\d{10}$/;

/**
 * Order edit modal. Reuses PhotoPicker + ClerkPinModal.
 * On save → PIN required → PUT /orders/{id} → onUpdated(newOrder).
 * On delete → PIN required → DELETE /orders/{id} → onDeleted(id).
 *
 * Props:
 *  - order: OrderOut | null  (null hides the modal)
 *  - onClose: () => void
 *  - onUpdated: (order) => void
 *  - onDeleted: (id) => void
 */
export default function EditOrderModal({ order, onClose, onUpdated, onDeleted }) {
  const [form, setForm] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePinOpen, setDeletePinOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!order) {
      setForm(null);
      setImageFile(null);
      setImagePreview(null);
      setImageRemoved(false);
      return;
    }
    setForm({
      full_name: order.full_name || "",
      phone: order.phone || "",
      address: order.address || "",
      delivery_time: isoToLocal(order.delivery_time),
      items: (order.items || []).join("\n"),
      notes: (order.notes || []).join("\n"),
      amount: order.amount ? String(order.amount) : "",
    });
  }, [order]);

  if (!order || !form) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = (file, preview) => {
    setImageFile(file);
    setImagePreview(preview);
    setImageRemoved(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Müşteri adı zorunludur");
    const phoneNorm = normalizePhone(form.phone);
    if (!phoneNorm || !PHONE_RE.test(phoneNorm)) return toast.error("Geçerli telefon: 05XXXXXXXXX");
    if (!form.delivery_time) return toast.error("Teslimat saati zorunludur");
    if (!form.address.trim()) return toast.error("Adres zorunludur");
    setPinOpen(true);
  };

  const submitUpdate = async (clerkPin) => {
    const items = form.items.split("\n").map((s) => s.trim()).filter(Boolean);
    const notes = form.notes.split("\n").map((s) => s.trim()).filter(Boolean);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("clerk_pin", clerkPin);
      fd.append("full_name", form.full_name.trim());
      fd.append("phone", normalizePhone(form.phone));
      fd.append("address", form.address.trim());
      fd.append("delivery_time", new Date(form.delivery_time).toISOString());
      fd.append("items", JSON.stringify(items));
      fd.append("notes", JSON.stringify(notes));
      if (form.amount) fd.append("amount", parseFloat(form.amount).toString());
      if (imageFile) fd.append("image", imageFile);
      const { data } = await api.put(`/orders/${order.id}`, fd);
      toast.success(`Güncellendi · ${data.updated_by_name}`);
      setPinOpen(false);
      onUpdated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async (clerkPin) => {
    setDeleting(true);
    try {
      const fd = new FormData();
      fd.append("clerk_pin", clerkPin);
      const { data } = await api.delete(`/orders/${order.id}`, { data: fd });
      toast.success(`Silindi · ${data.deleted_by || "personel"}`);
      setDeletePinOpen(false);
      setDeleteConfirmOpen(false);
      onDeleted?.(order.id);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      data-testid="edit-order-modal"
      className="fixed inset-0 z-50 bg-zinc-950/80"
      onClick={onClose}
    >
      <div className="absolute inset-0 overflow-y-auto overscroll-contain">
        <div className="min-h-full flex items-start sm:items-center justify-center p-0 sm:p-6">
          <div
            className="bg-white border-2 border-zinc-950 brutal-shadow-dark w-full sm:max-w-lg my-0 sm:my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="sticky z-10 flex items-center justify-between border-b-2 border-zinc-200 bg-white px-5 py-3"
              style={{ top: 0, paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-amber-500 text-zinc-950 flex items-center justify-center">
                  <Pencil strokeWidth={3} size={14} />
                </div>
                <h2 className="text-base font-black tracking-tight text-zinc-950 uppercase">Siparişi Güncelle</h2>
              </div>
              <button data-testid="close-edit-modal" onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-950" disabled={submitting}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]" data-testid="edit-order-form">
          <Field label="Müşteri Adı *">
            <input
              data-testid="edit-full-name"
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
              className="brutalist-input"
              required
            />
          </Field>
          <Field label="Telefon *">
            <input
              data-testid="edit-phone"
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              className="brutalist-input font-mono"
              required
            />
          </Field>
          <Field label="Adres *">
            <textarea
              data-testid="edit-address"
              rows={2}
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              className="brutalist-input resize-none"
              required
            />
          </Field>
          <Field label="Teslimat Saati *">
            <DateTimeQuickPicker
              value={form.delivery_time}
              onChange={(v) => update("delivery_time", v)}
              testIdPrefix="edit-delivery"
            />
          </Field>
          <Field label="Ürünler — Her satıra bir ürün">
            <textarea
              data-testid="edit-items"
              rows={4}
              value={form.items}
              onChange={(e) => update("items", e.target.value)}
              className="brutalist-input resize-none font-mono text-[15px]"
              placeholder={"2 kg kıyma\n1 kg kuşbaşı"}
            />
          </Field>
          <Field label="Açıklamalar / Özel Notlar">
            <textarea
              data-testid="edit-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="brutalist-input resize-none font-mono text-[15px]"
              placeholder={"Pos cihazı götür"}
            />
          </Field>
          <Field label="Sipariş Kağıdı Fotoğrafı (yeni eklemek için)">
            {!imageFile && order.has_image && !imageRemoved && (
              <div className="mb-2 flex items-center gap-3 border-2 border-zinc-200 p-2">
                <div className="w-20 h-20 bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 font-mono text-[10px] uppercase tracking-wider">
                  Mevcut
                </div>
                <div className="flex-1 min-w-0 text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                  Mevcut fotoğraf korunuyor. Değiştirmek için yeni fotoğraf seçin.
                </div>
              </div>
            )}
            <PhotoPicker
              file={imageFile}
              preview={imagePreview}
              onChange={handleImage}
              testIdPrefix="edit-photo"
              cameraLabel="Sipariş Kağıdı Fotoğrafı Çek"
              galleryLabel="Galeriden Sipariş Kağıdı Seç"
            />
          </Field>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="edit-cancel"
              onClick={onClose}
              disabled={submitting || deleting}
              className="flex-1 py-3 border-2 border-zinc-300 text-zinc-700 text-xs font-bold uppercase tracking-widest hover:border-zinc-950 hover:text-zinc-950 disabled:opacity-60"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              data-testid="edit-submit"
              disabled={submitting || deleting}
              className="flex-1 bg-amber-500 text-zinc-950 text-xs font-black uppercase tracking-widest py-3 border-b-4 border-amber-700 active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-60"
            >
              Güncelle
            </button>
          </div>

          <button
            type="button"
            data-testid="edit-delete"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={submitting || deleting}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 py-3 border-2 border-red-500 text-red-700 bg-red-50 text-xs font-black uppercase tracking-widest hover:bg-red-100 transition-colors disabled:opacity-60"
          >
            <Trash2 size={14} strokeWidth={2.5} />
            Siparişi Sil
          </button>
        </form>

        <ClerkPinModal
          open={pinOpen}
          busy={submitting}
          title="Güncellemeyi Onayla"
          submitLabel="Güncelle"
          onClose={() => { if (!submitting) setPinOpen(false); }}
          onSubmit={submitUpdate}
        />

        {deleteConfirmOpen && (
          <div
            data-testid="delete-confirm-overlay"
            className="fixed inset-0 z-[60] bg-zinc-950/80 flex items-center justify-center p-4"
            onClick={() => { if (!deleting) setDeleteConfirmOpen(false); }}
          >
            <div
              className="bg-white border-2 border-red-600 brutal-shadow-dark w-full max-w-sm p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-red-600 text-white flex items-center justify-center">
                  <Trash2 strokeWidth={2.5} size={18} />
                </div>
                <h3 className="text-base font-black uppercase tracking-tight text-zinc-950">Siparişi Sil?</h3>
              </div>
              <p className="text-sm text-zinc-700 mb-2">
                <b className="text-zinc-950">{order.full_name}</b> isimli müşterinin siparişi <b>kalıcı olarak silinecek</b>.
              </p>
              <p className="text-xs text-zinc-500 mb-4">Bu işlem geri alınamaz. Devam etmek için personel şifrenizi girmeniz gerekecek.</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="delete-cancel"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={deleting}
                  className="flex-1 py-2.5 border-2 border-zinc-300 text-zinc-700 text-xs font-bold uppercase tracking-widest hover:border-zinc-950 disabled:opacity-60"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  data-testid="delete-confirm"
                  onClick={() => { setDeleteConfirmOpen(false); setDeletePinOpen(true); }}
                  disabled={deleting}
                  className="flex-1 py-2.5 bg-red-600 text-white text-xs font-black uppercase tracking-widest border-b-4 border-red-800 active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-60"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        )}

        <ClerkPinModal
          open={deletePinOpen}
          busy={deleting}
          title="Silmeyi Onayla"
          submitLabel="Sil"
          onClose={() => { if (!deleting) setDeletePinOpen(false); }}
          onSubmit={submitDelete}
        />
      </div>
        </div>
      </div>
    </div>
  );
}

function isoToLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-700 block mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
